/**
 * Cookie consent record, shared with the marketing website (georgia-app-prod/lib/consent.ts).
 * Both projects read and write the same cookie, so the name, the JSON shape and the version
 * must stay identical in the two files.
 */

export const CONSENT_COOKIE = 'georgia_consent'
/** Bump when the categories or what they cover change: everyone is asked again. */
export const CONSENT_VERSION = 1
const CONSENT_MAX_AGE_DAYS = 180

export type ConsentChoices = {
  functional: boolean
  analytics: boolean
}

export type ConsentRecord = ConsentChoices & {
  /** When the choice was made, ms since epoch */
  timestamp: number
}

/**
 * Parent domain to store the cookie on, e.g. `.georgia-app.com`, so one choice covers the website
 * and the app. Unset (local dev) keeps the cookie on the current host only.
 */
const cookieDomain = (): string | undefined => {
  const domain = import.meta.env.VITE_COOKIE_DOMAIN as string | undefined
  if (!domain) return undefined
  // A cookie for a domain the page is not on is silently dropped by the browser
  return window.location.hostname.endsWith(domain.replace(/^\./, '')) ? domain : undefined
}

export const parseConsent = (cookieHeader: string): ConsentRecord | null => {
  const raw = cookieHeader
    .split('; ')
    .find((part) => part.startsWith(`${CONSENT_COOKIE}=`))
    ?.slice(CONSENT_COOKIE.length + 1)
  if (!raw) return null

  try {
    const data = JSON.parse(decodeURIComponent(raw))
    if (data?.v !== CONSENT_VERSION) return null
    return { functional: data.f === true, analytics: data.a === true, timestamp: Number(data.t) || 0 }
  } catch {
    return null
  }
}

export const readConsent = (): ConsentRecord | null => parseConsent(document.cookie)

export const writeConsent = (choices: ConsentChoices): ConsentRecord => {
  const record: ConsentRecord = { ...choices, timestamp: Date.now() }
  const value = encodeURIComponent(JSON.stringify({ v: CONSENT_VERSION, f: record.functional, a: record.analytics, t: record.timestamp }))
  const attributes = [`Max-Age=${CONSENT_MAX_AGE_DAYS * 24 * 60 * 60}`, 'Path=/', 'SameSite=Lax']
  const domain = cookieDomain()
  if (domain) attributes.push(`Domain=${domain}`)
  if (window.location.protocol === 'https:') attributes.push('Secure')

  document.cookie = `${CONSENT_COOKIE}=${value}; ${attributes.join('; ')}`
  return record
}

/** Removes cookies whose name starts with `prefix`, on this host and on every parent domain. */
export const deleteCookiesByPrefix = (prefix: string) => {
  const names = document.cookie
    .split('; ')
    .map((part) => part.split('=')[0])
    .filter((name) => name.startsWith(prefix))
  if (names.length === 0) return

  const labels = window.location.hostname.split('.')
  const domains = labels.slice(0, -1).map((_, i) => `.${labels.slice(i).join('.')}`)

  for (const name of names) {
    document.cookie = `${name}=; Max-Age=0; Path=/`
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${domain}`
    }
  }
}
